import { Fragment, useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api } from '../../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Select, Spinner, EmptyState } from '../ui';
import { fmtMonth, fmtPct, fmtCurrencyWhole } from '../../lib/fmt';

/** Read view: how billable capacity (hours × utilisation × ramp × rate) builds
 *  T&M revenue per department, per month. Sits in the Revenue section. */
export function TmBuild({ planId }: { planId: string }) {
  const { data, isLoading } = useQuery(() => api.fpa.plans.revenueBuild(planId), [planId]);
  const build = data?.build ?? [];
  const months = Array.from(new Set(build.map((b) => b.period_month))).sort();
  const [month, setMonth] = useState('');
  const m = month && months.includes(month) ? month : (months[0] ?? '');
  const rowsForMonth = build.filter((b) => b.period_month === m);
  const total = Math.round(rowsForMonth.reduce((s, b) => s + b.amount, 0));

  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>T&amp;M revenue build</div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>billable capacity → revenue: hours × utilisation × ramp × rate</span>
        {months.length > 0 && <span style={{ marginLeft: 'auto' }}><Select value={m} onChange={setMonth}>{months.map((mm) => <option key={mm} value={mm}>{fmtMonth(mm)}</option>)}</Select></span>}
      </CardHeader>
      {isLoading ? <Spinner />
        : build.length === 0 ? <EmptyState message="No T&M revenue — recompute the plan (needs billable headcount with a bill rate)." />
        : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead><tr><Th>Department / role</Th><Th right>Hours</Th><Th right>Util</Th><Th right>Ramp</Th><Th right>Rate</Th><Th right>Revenue</Th></tr></thead>
              <tbody>
                {rowsForMonth.map((b) => {
                  const gross = b.contributions.reduce((s, c) => s + c.contribution, 0);
                  return (
                    <Fragment key={b.department_id}>
                      <tr><td colSpan={6} style={{ padding: '5px 12px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--color-text-muted)', background: 'var(--color-bg)', borderTop: '0.5px solid var(--color-border)' }}>{b.department_id}</td></tr>
                      {b.contributions.length === 0 && <Tr><Td muted>no billable heads</Td><Td> </Td><Td> </Td><Td> </Td><Td> </Td><Td right muted>—</Td></Tr>}
                      {b.contributions.map((c, i) => (
                        <Tr key={i}>
                          <Td>{c.role_label}</Td>
                          <Td right muted>{Math.round(c.hours)}</Td>
                          <Td right muted>{fmtPct(c.util)}</Td>
                          <Td right muted>{fmtPct(c.ramp_productivity)}</Td>
                          <Td right muted>${Math.round(c.rate)}/hr</Td>
                          <Td right>{fmtCurrencyWhole(c.contribution)}</Td>
                        </Tr>
                      ))}
                      {b.sub_servicing_pct > 0 && (
                        <Tr><Td muted>− subscription servicing ({fmtPct(b.sub_servicing_pct)})</Td><Td> </Td><Td> </Td><Td> </Td><Td> </Td><Td right muted>−{fmtCurrencyWhole(Math.round(gross - b.amount))}</Td></Tr>
                      )}
                      <Tr><Td bold>{b.department_id} subtotal</Td><Td> </Td><Td> </Td><Td> </Td><Td> </Td><Td right bold>{fmtCurrencyWhole(b.amount)}</Td></Tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
            <div style={{ padding: '8px 16px', fontSize: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Total T&amp;M, {fmtMonth(m)}:</span> <strong>{fmtCurrencyWhole(total)}</strong>
            </div>
          </div>
        )}
    </Card>
  );
}
